import React, { useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/react-hooks';
import { Alert, Button, Card, Table, Tabs, Tag, Typography } from 'antd';
import moment from 'moment';

import WeekPicker from '../../common/components/WeekPicker';
import {
  GET_TIMESHEET_SNAPSHOT_DISCREPANCIES,
  GET_BUDGET_INTEGRITY_CHECK,
  GET_TIMEOFF_INTEGRITY_CHECK,
} from '../queries';

const { Title, Text } = Typography;

// ─── Timesheet vs Snapshot View ──────────────────────────────────────────────

const TimesheetVsSnapshotView = () => {
  const [weekEndDate, setWeekEndDate] = useState(() => moment().endOf('isoWeek').format('YYYYMMDD'));
  const [runCheck, { data, loading, error }] = useLazyQuery(GET_TIMESHEET_SNAPSHOT_DISCREPANCIES, {
    fetchPolicy: 'network-only',
  });

  const allTimesheets = data?.admin?.timesheetSnapshotDiscrepancies ?? [];
  const discrepancies = allTimesheets.filter((d) => {
    const fields = (d.discrepancies || []).map((disc) => disc.field);
    return fields.includes('status') && fields.includes('totalHours');
  });

  const handleRunCheck = () => {
    runCheck({
      variables: {
        startDate: weekEndDate,
        endDate: weekEndDate,
      },
    });
  };

  const discrepancyColumns = useMemo(
    () => [
      {
        title: 'Timesheet ID',
        dataIndex: 'timesheetId',
        key: 'timesheetId',
        render: (id) => (id ? String(id).slice(-8) : '-'),
      },
      {
        title: 'User Email',
        dataIndex: 'userEmail',
        key: 'userEmail',
      },
      {
        title: 'End Date',
        dataIndex: 'endDate',
        key: 'endDate',
      },
      {
        title: 'Discrepancies',
        dataIndex: 'discrepancies',
        key: 'discrepancies',
        render: (items) =>
          items?.length > 0
            ? items.map((d, i) => (
                <div key={i} className="text-sm">
                  <span className="font-medium">{d.field}:</span> expected {d.expected}, actual{' '}
                  {d.actual}
                </div>
              ))
            : '-',
      },
    ],
    [],
  );

  const comparisonColumns = useMemo(
    () => [
      {
        title: 'Timesheet ID',
        dataIndex: 'timesheetId',
        key: 'timesheetId',
        render: (id) => (id ? String(id).slice(-8) : '-'),
      },
      {
        title: 'User Email',
        dataIndex: 'userEmail',
        key: 'userEmail',
      },
      {
        title: 'End Date',
        dataIndex: 'endDate',
        key: 'endDate',
      },
      {
        title: 'Timesheet Status',
        dataIndex: 'timesheetStatus',
        key: 'timesheetStatus',
        render: (val) => val || '-',
      },
      {
        title: 'Snapshot Status',
        dataIndex: 'snapshotStatus',
        key: 'snapshotStatus',
        render: (val, record) => (record.missingSnapshot ? 'N/A' : val || '-'),
      },
      {
        title: 'Timesheet Hours',
        dataIndex: 'timesheetTotalHours',
        key: 'timesheetTotalHours',
        render: (val) => (val != null ? val.toFixed(2) : '-'),
      },
      {
        title: 'Snapshot Hours',
        dataIndex: 'snapshotTotalHours',
        key: 'snapshotTotalHours',
        render: (val, record) =>
          record.missingSnapshot ? 'N/A' : val != null ? val.toFixed(2) : '-',
      },
    ],
    [],
  );

  return (
    <Card title="Timesheet vs Snapshot">
      <div className="flex items-center gap-4 flex-wrap">
        <WeekPicker endDate={weekEndDate} onDateChange={setWeekEndDate} loading={loading} />
        <Button type="primary" onClick={handleRunCheck} loading={loading}>
          Run Discrepancy Check
        </Button>
      </div>

      {error && (
        <Alert
          className="mt-4"
          type="error"
          showIcon
          message="Error"
          description={error.message || 'Failed to run check'}
        />
      )}

      {data && !error && (
        <div className="mt-4">
          {discrepancies.length === 0 ? (
            <>
              <Alert
                type="success"
                showIcon
                message="No discrepancies found"
                description={`All ${allTimesheets.length} timesheet(s) in the selected week match their snapshot data.`}
                className="mb-4"
              />
              {allTimesheets.length > 0 && (
                <Table
                  dataSource={allTimesheets}
                  columns={comparisonColumns}
                  rowKey="timesheetId"
                  pagination={{ pageSize: 20 }}
                  size="small"
                />
              )}
            </>
          ) : (
            <>
              <Alert
                className="mb-4"
                type="warning"
                showIcon
                message={`${discrepancies.length} timesheet(s) with both status and total hours differences`}
              />
              <Table
                dataSource={discrepancies}
                columns={discrepancyColumns}
                rowKey="timesheetId"
                pagination={{ pageSize: 20 }}
              />
            </>
          )}
        </div>
      )}
    </Card>
  );
};

// ─── Project Budget Integrity View ───────────────────────────────────────────

const BudgetIntegrityView = () => {
  const [runCheck, { data, loading, error }] = useLazyQuery(GET_BUDGET_INTEGRITY_CHECK, {
    fetchPolicy: 'network-only',
  });

  const results = data?.admin?.budgetIntegrityCheck ?? [];

  const columns = useMemo(
    () => [
      {
        title: 'Project',
        dataIndex: 'projectName',
        key: 'projectName',
        sorter: (a, b) => (a.projectName || '').localeCompare(b.projectName || ''),
      },
      {
        title: 'Client',
        dataIndex: 'clientName',
        key: 'clientName',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (val) => val || '-',
      },
      {
        title: 'Budget',
        dataIndex: 'budget',
        key: 'budget',
        render: (val) => (val != null ? `$${val.toLocaleString()}` : '-'),
        align: 'right',
      },
      {
        title: 'Stored Budget Used',
        dataIndex: 'storedBudgetUsed',
        key: 'storedBudgetUsed',
        render: (val) => (val != null ? `${val.toFixed(1)}%` : '-'),
        align: 'right',
      },
      {
        title: 'Computed Budget Used',
        dataIndex: 'computedBudgetUsed',
        key: 'computedBudgetUsed',
        render: (val) => (val != null ? `${val.toFixed(1)}%` : '-'),
        align: 'right',
      },
      {
        title: 'Discrepancy',
        dataIndex: 'discrepancyPct',
        key: 'discrepancyPct',
        render: (val) => {
          if (val == null) return '-';
          let color = 'green';
          if (val > 5) color = 'red';
          else if (val > 1) color = 'orange';
          return <Tag color={color}>{val.toFixed(2)} pp</Tag>;
        },
        sorter: (a, b) => (a.discrepancyPct ?? 0) - (b.discrepancyPct ?? 0),
        defaultSortOrder: 'descend',
        align: 'right',
      },
      {
        title: 'Stored Cost',
        dataIndex: 'storedTotalCost',
        key: 'storedTotalCost',
        render: (val) => (val != null ? `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '-'),
        align: 'right',
      },
      {
        title: 'Computed Cost',
        dataIndex: 'computedTotalCost',
        key: 'computedTotalCost',
        render: (val) => (val != null ? `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '-'),
        align: 'right',
      },
      {
        title: 'Last Updated',
        dataIndex: 'budgetUsedUpdatedAt',
        key: 'budgetUsedUpdatedAt',
        render: (val) => (val ? moment(val).fromNow() : 'Never'),
      },
    ],
    [],
  );

  return (
    <Card title="Project Budget Integrity">
      <Text type="secondary" className="block mb-4">
        Compares stored budget values against live recalculations for all active projects with budgets.
        Flags projects where the stored budgetUsed, totalCost, or totalApprovedHours differ from the
        computed values.
      </Text>

      <Button type="primary" onClick={() => runCheck()} loading={loading}>
        Run Budget Check
      </Button>

      {error && (
        <Alert
          className="mt-4"
          type="error"
          showIcon
          message="Error"
          description={error.message || 'Failed to run budget integrity check'}
        />
      )}

      {data && !error && (
        <div className="mt-4">
          {results.length === 0 ? (
            <Alert
              type="success"
              showIcon
              message="No budget discrepancies found"
              description="All project budgets match their computed values."
            />
          ) : (
            <>
              <Alert
                className="mb-4"
                type="warning"
                showIcon
                message={`${results.length} project(s) with budget discrepancies`}
                description="These projects have stored budget values that differ from their live calculations. Consider running a budget recalculation."
              />
              <Table
                dataSource={results}
                columns={columns}
                rowKey="projectId"
                pagination={{ pageSize: 20 }}
                size="small"
                scroll={{ x: 1200 }}
              />
            </>
          )}
        </div>
      )}
    </Card>
  );
};

// ─── Time-Off Balance Integrity View ─────────────────────────────────────────

const TimeOffIntegrityView = () => {
  const [runCheck, { data, loading, error }] = useLazyQuery(GET_TIMEOFF_INTEGRITY_CHECK, {
    fetchPolicy: 'network-only',
  });

  const result = data?.admin?.timeOffIntegrityCheck;

  const issueColumns = useMemo(
    () => [
      {
        title: 'Employee',
        dataIndex: 'userName',
        key: 'userName',
        render: (val, record) => (
          <div>
            <div className="font-medium">{val || '-'}</div>
            <div className="text-xs text-gray-500">{record.userEmail}</div>
          </div>
        ),
      },
      {
        title: 'Time-Off Type',
        dataIndex: 'timeOffType',
        key: 'timeOffType',
      },
      {
        title: 'Issue',
        dataIndex: 'issueType',
        key: 'issueType',
        render: (val) => {
          const labels = {
            orphaned_taken: { label: 'Orphaned Transaction', color: 'red' },
            duplicate: { label: 'Duplicate', color: 'orange' },
            negative_balance: { label: 'Negative Balance', color: 'volcano' },
          };
          const config = labels[val] || { label: val, color: 'default' };
          return <Tag color={config.color}>{config.label}</Tag>;
        },
      },
      {
        title: 'Details',
        dataIndex: 'details',
        key: 'details',
        width: 400,
      },
      {
        title: 'Transaction ID',
        dataIndex: 'transactionId',
        key: 'transactionId',
        render: (val) => (val ? <span className="text-xs font-mono">{val.length > 30 ? val.slice(0, 30) + '...' : val}</span> : '-'),
      },
    ],
    [],
  );

  return (
    <Card title="Time-Off Balance Integrity">
      <Text type="secondary" className="block mb-4">
        Checks for orphaned time-off transactions (TAKEN entries linked to non-Approved or missing
        timesheets), duplicate transactions, and active employees with negative balances.
      </Text>

      <Button type="primary" onClick={() => runCheck()} loading={loading}>
        Run Time-Off Check
      </Button>

      {error && (
        <Alert
          className="mt-4"
          type="error"
          showIcon
          message="Error"
          description={error.message || 'Failed to run time-off integrity check'}
        />
      )}

      {result && !error && (
        <div className="mt-4">
          {result.totalIssues === 0 ? (
            <Alert
              type="success"
              showIcon
              message="No time-off issues found"
              description={`Checked ${result.totalUsersChecked} active users. All time-off balances and transactions are consistent.`}
            />
          ) : (
            <>
              <Alert
                className="mb-4"
                type="warning"
                showIcon
                message={`${result.totalIssues} issue(s) found across ${result.totalUsersChecked} active users`}
              />

              <div className="flex gap-4 mb-4 flex-wrap">
                <Card size="small" className="flex-1 min-w-[200px]">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-500">
                      {result.orphanedTransactions?.length || 0}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Orphaned Transactions</div>
                  </div>
                </Card>
                <Card size="small" className="flex-1 min-w-[200px]">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-500">
                      {result.duplicateTransactions?.length || 0}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Duplicate Transactions</div>
                  </div>
                </Card>
                <Card size="small" className="flex-1 min-w-[200px]">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">
                      {result.negativeBalances?.length || 0}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Negative Balances</div>
                  </div>
                </Card>
              </div>

              {result.orphanedTransactions?.length > 0 && (
                <div className="mb-6">
                  <Title level={5}>Orphaned Transactions</Title>
                  <Text type="secondary" className="block mb-2">
                    TAKEN transactions linked to timesheets that are missing, deleted, or not in Approved status.
                    These deductions may be incorrectly reducing balances.
                  </Text>
                  <Table
                    dataSource={result.orphanedTransactions}
                    columns={issueColumns}
                    rowKey={(r) => `${r.transactionId}-${r.userId}`}
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                </div>
              )}

              {result.duplicateTransactions?.length > 0 && (
                <div className="mb-6">
                  <Title level={5}>Duplicate Transactions</Title>
                  <Text type="secondary" className="block mb-2">
                    Multiple identical transactions (same user, date, type, and hours). These may be caused
                    by race conditions during concurrent timesheet approvals.
                  </Text>
                  <Table
                    dataSource={result.duplicateTransactions}
                    columns={issueColumns}
                    rowKey={(r) => `${r.transactionId}-${r.userId}-${r.timeOffType}`}
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                </div>
              )}

              {result.negativeBalances?.length > 0 && (
                <div className="mb-6">
                  <Title level={5}>Negative Balances</Title>
                  <Text type="secondary" className="block mb-2">
                    Active employees whose time-off balance has gone negative. This could indicate
                    missing EARNED or ROLLOVER transactions, or duplicate TAKEN entries.
                  </Text>
                  <Table
                    dataSource={result.negativeBalances}
                    columns={issueColumns}
                    rowKey={(r) => `${r.userId}-${r.timeOffType}`}
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const { TabPane } = Tabs;

const AdminDashboardPage = () => {
  return (
    <div className="p-6">
      <Title level={3}>Data Checks</Title>
      <Text type="secondary">Run integrity checks to identify stale, inconsistent, or corrupt data.</Text>

      <div className="mt-4">
        <Tabs defaultActiveKey="timesheet-snapshot">
          <TabPane tab="Timesheet vs Snapshot" key="timesheet-snapshot">
            <TimesheetVsSnapshotView />
          </TabPane>
          <TabPane tab="Budget Integrity" key="budget-integrity">
            <BudgetIntegrityView />
          </TabPane>
          <TabPane tab="Time-Off Balances" key="timeoff-integrity">
            <TimeOffIntegrityView />
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
